import type { Vec3 } from '../../geo/types';
import type { VertexStyle } from '../mesh/meshBuilder';
import { PATTERN } from '../../render/patterns/patternIds';
import type { ShipType } from './shipTemplates';

/**
 * The width of a strake (docs/plans/ships.md 3): about 0.30 m (0.25-0.40) carvel on the big ships,
 * as the woodcut draws them; clinker 0.20-0.25 m on the boeier and 0.18-0.20 m on the rowing boat
 * (woodcut small craft; Verweij, Waldus and Van Holk 2012), 0.28 m on the praam's sides.
 */
export const STRAKE_WIDTH: Readonly<Record<ShipType, number>> = {
  kraeck: 0.3,
  hulk: 0.3,
  bark: 0.3,
  boeier: 0.225,
  rowing: 0.19,
  praam: 0.28,
};

const CLINKER: ReadonlySet<ShipType> = new Set<ShipType>(['boeier', 'rowing', 'praam']);

/** Whole strakes a side over a girth, at least one. */
export function strakesOver(girth: number, width: number): number {
  return Math.max(1, Math.round(girth / width));
}

/** A hull's planking over `girth` metres from keel to gunwale: carvel on the big ships, clinker
    on the boats (STYLE 15.2 hullCarvel, hullClinker). */
export function plankingStyle(style: VertexStyle, type: ShipType, girth: number): VertexStyle {
  return {
    ...style,
    pattern: CLINKER.has(type) ? PATTERN.hullClinker : PATTERN.hullCarvel,
    extent: [strakesOver(girth, STRAKE_WIDTH[type]), 0],
  };
}

/** A sail's cloths, its bonnet laced at the preset's share of `depth` down from the head; a sail
    without a bonnet takes depth 0 (STYLE 15.2 sailcloth). */
export function sailclothStyle(style: VertexStyle, depth: number): VertexStyle {
  return { ...style, pattern: PATTERN.sailcloth, extent: [depth, 0] };
}

function runs(section: readonly Vec3[]): number[] {
  return section.reduce<number[]>((sums, p, i) => {
    if (i === 0) return [0];
    const q = section[i - 1]!;
    return [...sums, sums[i - 1]! + Math.hypot(p[0] - q[0], p[1] - q[1], p[2] - q[2])];
  }, []);
}

/** A section's length along its points. */
export function girthLength(section: readonly Vec3[]): number {
  return runs(section)[section.length - 1]!;
}

/** The share of a section's length from its first point at which each of its points lies. */
export function girthFractions(section: readonly Vec3[]): number[] {
  const along = runs(section);
  const total = along[along.length - 1]!;
  return along.map((length) => length / total);
}
